import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BASE_ACTIONS, getPostAuthorization, usePostSingularAction } from "@sprout/client-components/dist/Post";
import { AssignmentDialog, LabelsDialog } from "@sprout/client-components/dist/Dialog_next";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import { useCallback, useMemo, useState } from "react";
import { ItemType } from "sprout-graphql";
import useAssignMany from "@sprout/client-components/dist/Dialog_next/AssignmentDialog/graphql/mutations/useAssignMany";
import { useFnRef } from "sprout-hooks";
import { getIsLargePointerDevice } from "sprout-ui-dom/dist/utils";
import useSetWhenForItems from "@sprout/client-graphql/dist/mutations/useSetWhenForItems";
import { usePopoverRegistry } from "sprout-ui-dom";
import { useSetItemsCompleted } from "@sprout/client-graphql";
import useArchiveTodo from "../graphql/mutations/useArchiveTodo";
import useConvertItemToPost from "../graphql/mutations/useConvertItemToPost";
import useDeleteTodo from "../graphql/mutations/useDeleteTodo";
import { usePostPageEditor } from "../PostPageEditorContext";
const todoSupportedActions = [
    ...BASE_ACTIONS,
    "ADD_TODO",
    "EDIT_TODO",
    //
    "SET_COMPLETED",
    "SET_INCOMPLETE",
    //
    "ARCHIVE",
    "DELETE",
    "CONVERT_TODO_TO_POST",
    "ASSIGN",
    "LABEL",
    "SELF_ASSIGN",
    "WHEN_TODAY",
    "WHEN_CLEAR",
    "WHEN",
    "DEADLINE", 
];
export function useTodoSingularAction(variables) {
    const { todos , workspaceMembers , labels , rootTopicId  } = variables;
    const currentUser = useCurrentUser();
    const currentUserId = currentUser === null || currentUser === void 0 ? void 0 : currentUser.id;
    const { dispatch  } = usePostPageEditor();
    //
    const users = useMemo(()=>currentUser ? [
            currentUser
        ] : [], [
        currentUser
    ]);
    const assignMany = useAssignMany(users);
    const assignManyRef = useFnRef(assignMany);
    const setItemsCompleted = useSetItemsCompleted();
    const archiveTodo = useArchiveTodo();
    const deleteTodo = useDeleteTodo();
    const convertItemToPost = useConvertItemToPost();
    //
    const [assignItemId, setAssignItemId] = useState();
    const selectedAssignItems = useMemo(()=>{
        const found = todos.find((todo)=>todo.id === assignItemId);
        if (found) return [
            found
        ];
        return [];
    }, [
        assignItemId,
        todos
    ]);
    const handleAssignClose = useCallback(()=>{
        return setAssignItemId(undefined);
    }, []);
    //
    const [labelItemId, setLabelItemId] = useState();
    const selectedLabelItems = useMemo(()=>{
        const found = todos.find((todo)=>todo.id === labelItemId);
        if (found) return [
            found
        ];
        return [];
    }, [
        labelItemId,
        todos
    ]);
    const handleLabelClose = useCallback(()=>{
        return setLabelItemId(undefined);
    }, []);
    const setWhenForItems = useSetWhenForItems();
    const getPopoverRegistry = usePopoverRegistry();
    const todoSingularAction = usePostSingularAction(useCallback((command, activeItem)=>{
        if (!activeItem) return false;
        const variant = getIsLargePointerDevice() ? "desktop" : "mobile";
        const authorization = getPostAuthorization({
            post: activeItem,
            currentUserId
        });
        const { completedAt , parentTopicId , id , isExplicitlyDeleted , isExplicitlyArchived ,  } = activeItem;
        switch(command){
            case "SELF_ASSIGN":
                if (authorization.assign && typeof currentUserId !== "undefined") {
                    const assigned = !!activeItem.assignments.map(({ assignee  })=>assignee).find((user)=>{
                        return (user === null || user === void 0 ? void 0 : user.id) === currentUserId;
                    });
                    assignManyRef.current({
                        assigneesToAdd: !assigned ? [
                            currentUserId
                        ] : [],
                        assigneesToRemove: assigned ? [
                            currentUserId
                        ] : [],
                        posts: [
                            activeItem
                        ]
                    });
                    return true;
                }
                return false;
            case "ADD_TODO":
                if (!authorization.addTodo) return false;
                dispatch({
                    type: "SET_EDITOR",
                    payload: {
                        editor: "CREATE_TODO",
                        editorPosition: completedAt ? Number.MAX_SAFE_INTEGER : id,
                        parentId: completedAt ? undefined : parentTopicId !== null && parentTopicId !== void 0 ? parentTopicId : undefined,
                        variant
                    }
                });
                return true;
            case "SET_COMPLETED":
            case "SET_INCOMPLETE":
                if (!authorization.addTodo) return false;
                setItemsCompleted({
                    items: [
                        activeItem
                    ],
                    completed: command === "SET_COMPLETED"
                });
                return true;
            case "EDIT_TODO":
                if (!authorization.addTodo) return false;
                dispatch({
                    type: "SET_EDITOR",
                    payload: {
                        editor: "EDIT_TODO",
                        editorPosition: id,
                        variant
                    }
                });
                return true;
            case "ARCHIVE":
                if (!authorization.archive) return false;
                archiveTodo({
                    variables: {
                        itemId: id,
                        isExplicitlyArchived: !isExplicitlyArchived
                    }
                });
                return true;
            case "DELETE":
                if (!authorization.trash) return false;
                deleteTodo({
                    variables: {
                        itemId: id,
                        isDeleted: !isExplicitlyDeleted
                    }
                });
                return true;
            case "CONVERT_TODO_TO_POST":
                if (!authorization.convertToPost) return false;
                convertItemToPost({
                    itemId: id,
                    newParentId: rootTopicId,
                    newType: ItemType.Post
                });
                return true;
            case "ASSIGN":
                if (!authorization.assign) return false;
                setAssignItemId(id);
                return true;
            case "LABEL":
                if (!authorization.addLabel) return false;
                setLabelItemId(id);
                return true;
            case "WHEN":
            case "DEADLINE":
                {
                    if (!authorization.schedule) return false;
                    const cache = getPopoverRegistry();
                    const handlers = cache[`${activeItem.id}#${command.toLowerCase()}`];
                    handlers === null || handlers === void 0 ? void 0 : handlers.onOpen();
                    return true;
                }
            case "WHEN_CLEAR":
                if (!authorization.schedule) return false;
                setWhenForItems({
                    items: [
                        activeItem
                    ],
                    when: null,
                    whenReminder: null
                });
                return true;
            case "WHEN_TODAY":
                if (!authorization.schedule) return false;
                setWhenForItems({
                    items: [
                        activeItem
                    ],
                    today: true
                });
                return true;
            default:
                return false;
        }
    }, [
        archiveTodo,
        assignManyRef,
        convertItemToPost,
        currentUserId,
        deleteTodo,
        dispatch,
        getPopoverRegistry,
        rootTopicId,
        setItemsCompleted,
        setWhenForItems, 
    ]));
    return {
        elements: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(AssignmentDialog, {
                    selectedItems: selectedAssignItems,
                    workspaceMembers: workspaceMembers,
                    open: typeof assignItemId !== "undefined",
                    onRequestClose: handleAssignClose
                }),
                /*#__PURE__*/ _jsx(LabelsDialog, {
                    selectedItems: selectedLabelItems,
                    open: typeof labelItemId !== "undefined",
                    onRequestClose: handleLabelClose,
                    labels: labels,
                    rootTopicId: rootTopicId
                })
            ]
        }),
        todoSingularAction,
        todoSupportedActions
    };
}
